import type { Project } from "../types";
import { APP_VERSION } from "../types";

interface ValidationResult {
	valid: boolean;
	errors: string[];
}

/**
 * Validates a scenario JSON file to ensure it has the required structure
 */
export function validateScenarioFile(data: any): ValidationResult {
	const errors: string[] = [];

	// Check if data exists
	if (!data || typeof data !== 'object') {
		return { valid: false, errors: ['Ogiltig fil: Inte ett giltigt JSON-objekt'] };
	}

	// Check meta
	if (!data.meta || typeof data.meta !== 'object') {
		errors.push('Saknar "meta" objekt');
	} else {
		// Validate meta fields
		if (!data.meta.name || typeof data.meta.name !== 'string') {
			errors.push('meta.name saknas eller är ogiltig');
		}
		if (!data.meta.startTime || typeof data.meta.startTime !== 'object') {
			errors.push('meta.startTime saknas eller är ogiltig');
		} else {
			if (typeof data.meta.startTime.hour !== 'number') errors.push('meta.startTime.hour måste vara ett nummer');
			if (typeof data.meta.startTime.minute !== 'number') errors.push('meta.startTime.minute måste vara ett nummer');
			if (typeof data.meta.startTime.second !== 'number') errors.push('meta.startTime.second måste vara ett nummer');
		}
		if (!data.meta.crimeTime || typeof data.meta.crimeTime !== 'object') {
			errors.push('meta.crimeTime saknas eller är ogiltig');
		}
	}

	// Check agents
	if (!data.agents || !Array.isArray(data.agents)) {
		errors.push('Saknar "agents" array');
	} else {
		data.agents.forEach((agent: any, index: number) => {
			if (!agent.id || typeof agent.id !== 'string') {
				errors.push(`Agent ${index}: Saknar eller ogiltigt id`);
			}
			if (!agent.label || typeof agent.label !== 'string') {
				errors.push(`Agent ${index}: Saknar eller ogiltigt label`);
			}
			if (!agent.segments || !Array.isArray(agent.segments)) {
				errors.push(`Agent ${index} (${agent.label || 'okänd'}): Saknar segments array`);
			}
		});
	}

	// Check clockAnnotations (optional but should be array if present)
	if (data.clockAnnotations !== undefined && !Array.isArray(data.clockAnnotations)) {
		errors.push('"clockAnnotations" måste vara en array');
	}

	// Check speedControls (optional but should be array if present)
	if (data.speedControls !== undefined && !Array.isArray(data.speedControls)) {
		errors.push('"speedControls" måste vara en array');
	}

	// Check analysis (optional but should be object with sections array if present)
	if (data.analysis !== undefined) {
		if (typeof data.analysis !== 'object' || data.analysis === null) {
			errors.push('"analysis" måste vara ett objekt');
		} else if (data.analysis.sections && !Array.isArray(data.analysis.sections)) {
			errors.push('"analysis.sections" måste vara en array');
		}
	}

	return {
		valid: errors.length === 0,
		errors
	};
}

/**
 * Attempts to fix common issues in scenario files
 */
export function sanitizeScenarioFile(data: any): Project {
	// Ensure meta exists with defaults
	const meta = data.meta || {};
	
	return {
		meta: {
			name: meta.name || 'Importerat scenario',
			startTime: meta.startTime || { hour: 23, minute: 0, second: 0 },
			crimeTime: meta.crimeTime || { hour: 23, minute: 21, second: 30 },
			soundEffects: typeof meta.soundEffects === 'boolean' ? meta.soundEffects : true,
			version: typeof meta.version === 'number' ? meta.version : APP_VERSION,
			createdAt: meta.createdAt || new Date().toISOString(),
			updatedAt: new Date().toISOString(),
			ownerUUID: meta.ownerUUID,
			slug: meta.slug,
			exportedAt: meta.exportedAt,
		},
		agents: Array.isArray(data.agents) ? data.agents : [],
		clockAnnotations: Array.isArray(data.clockAnnotations) ? data.clockAnnotations : [],
		speedControls: Array.isArray(data.speedControls) ? data.speedControls : [],
		analysis: data.analysis && typeof data.analysis === 'object' ? data.analysis : undefined,
		viewportPins: Array.isArray(data.viewportPins) ? data.viewportPins : [],
		autoPan: typeof data.autoPan === 'boolean' ? data.autoPan : true,
	};
}
